import { useCallback, useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { API, api, apiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);

function Grid({ head, rows, empty, testId }) {
  if (!rows.length)
    return <p className="py-10 text-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{empty}</p>;
  return (
    <div className="surface overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow>{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((c, j) => (
                <TableCell key={j} className={j === 0 ? "text-xs" : "text-xs"}>{c}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function GstReports() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/gst/reports/summary?month=${m}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month); }, [load, month]);

  const invRows = (list) => list.map((e) => [
    e.gstin || "—", e.customer, e.invoice_number, e.invoice_date, inr(e.invoice_value),
    e.place_of_supply, e.supply_type, inr(e.taxable), inr(e.cgst), inr(e.sgst), inr(e.igst),
  ]);

  return (
    <div>
      <PageHeader
        testId="gst-reports-header"
        title="GST reports"
        subtitle="GSTR-1 style summary for your accountant — B2B, B2C, HSN and document series"
        actions={
          <>
            <Input type="month" value={month} className="w-[150px]"
              data-testid="gst-month-input" onChange={(e) => setMonth(e.target.value)} />
            <Button variant="outline" className="gap-1.5" data-testid="gst-csv-button"
              onClick={() => window.open(`${API}/gst/reports/csv?month=${month}&section=all`, "_blank")}>
              <FileSpreadsheet className="h-4 w-4" /> Excel / CSV
            </Button>
            <Button variant="outline" className="gap-1.5" data-testid="gst-pdf-button"
              onClick={() => window.open(`${API}/gst/reports/pdf?month=${month}`, "_blank")}>
              <Download className="h-4 w-4" /> PDF
            </Button>
          </>
        }
      />

      {loading || !data ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 border-b border-border sm:grid-cols-6"
            data-testid="gst-totals">
            {[
              ["Invoices", data.totals.invoices],
              ["Taxable", inr(data.totals.taxable)],
              ["CGST", inr(data.totals.cgst)],
              ["SGST", inr(data.totals.sgst)],
              ["IGST", inr(data.totals.igst)],
              ["Invoice value", inr(data.totals.total)],
            ].map(([k, v]) => (
              <div key={k} className="kpi border-b border-r border-border">
                <div className="overline">{k}</div>
                <div className="mt-1.5 font-mono-num text-lg font-semibold">{v}</div>
              </div>
            ))}
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{data.company.name}</Badge>
              <Badge variant="outline">GSTIN {data.company.gstin || "—"}</Badge>
              <Badge variant="outline">{data.from} to {data.to}</Badge>
              {data.cancelled_invoices > 0 && (
                <Badge variant="destructive">{data.cancelled_invoices} cancelled</Badge>
              )}
            </div>

            <Tabs defaultValue="b2b">
              <TabsList className="flex-wrap" data-testid="gst-tabs">
                <TabsTrigger value="b2b" data-testid="tab-b2b">B2B ({data.b2b.length})</TabsTrigger>
                <TabsTrigger value="b2c" data-testid="tab-b2c">B2C ({data.b2c.length})</TabsTrigger>
                <TabsTrigger value="hsn" data-testid="tab-hsn">HSN ({data.hsn.length})</TabsTrigger>
                <TabsTrigger value="docs" data-testid="tab-docs">Document series</TabsTrigger>
              </TabsList>
              <TabsContent value="b2b">
                <Grid testId="gst-b2b-table"
                  head={["GSTIN", "Customer", "Invoice", "Date", "Value", "Place of supply",
                         "Supply", "Taxable", "CGST", "SGST", "IGST"]}
                  rows={invRows(data.b2b)}
                  empty="No registered-customer (B2B) invoices in this month." />
              </TabsContent>
              <TabsContent value="b2c">
                <Grid testId="gst-b2c-table"
                  head={["GSTIN", "Customer", "Invoice", "Date", "Value", "Place of supply",
                         "Supply", "Taxable", "CGST", "SGST", "IGST"]}
                  rows={invRows(data.b2c)}
                  empty="No unregistered-customer (B2C) invoices in this month." />
              </TabsContent>
              <TabsContent value="hsn">
                <Grid testId="gst-hsn-table"
                  head={["HSN / SAC", "Rate %", "Quantity", "Unit", "Taxable", "CGST", "SGST",
                         "IGST"]}
                  rows={data.hsn.map((h) => [h.hsn_sac, h.gst_rate, h.quantity, h.unit || "—",
                    inr(h.taxable), inr(h.cgst), inr(h.sgst), inr(h.igst)])}
                  empty="No HSN data for this month." />
              </TabsContent>
              <TabsContent value="docs">
                <Grid testId="gst-docs-table"
                  head={["Series", "From", "To", "Count", "Cancelled"]}
                  rows={data.document_series.map((d) => [d.series, d.from, d.to, d.count,
                    d.cancelled])}
                  empty="No invoices issued in this month." />
              </TabsContent>
            </Tabs>
          </div>
        </>
      )}
    </div>
  );
}
